import { acceptInvite, inviteRegister } from './../../Api/accountApi';
import { apiErrorList } from 'Services/restAdviser'
import { enableOnboarding } from 'Services/onboardingManager'
import { GetterTree, MutationTree, ActionTree } from 'vuex'
import { parseEncodedAuth, parseApiAuth, parseTwoFactorAuthStatus, parseSelectedOptionTwoFactorAuth } from 'Parsers/authParser'
import { StoreState, AccessLoginResult, AccessCredentials } from 'Types'
import { updateToken,
      getInfoTwoFactorAuth,
      registerTokenSendingMethod,
      sendTokenToLogin,
      loginTokenTWoFactorAuth,
      validateTokenTWoFactorAuth,
      disableTokenTWoFactorAuth,
      setBackupTokenTWoFactorAuth,
      getStatusTokenTWoFactorAuth,
      getSelectedTwoFactorOptions } from 'Api/accountApi'
import accountApi from 'Api/accountApi'
import AccountState from 'Types/_Store/Account'
import jsonWebToken from 'Services/jsonWebToken'
import { notifyError } from 'Services/vueNotifier'

export const state: AccountState = {
  authenticated: false,
  token: null,

  user: {
    id: null,
    name: '',
    email: '',
    avatar: '',
    userAuthenticatedRole: '',
    rate: 0,
  },

  company: {
    isActive: false,
    id: null,
    name: '',
    logo: '',
    rate: 0,
    waitingRegistration: false
  },

  status: {
    isDirty: null,
    isNewUser: null,
  },

  requesting: {
    logout: false,
    login: false,
    twoFactorAuth: false,
    selectedTwoFactorOption: false,
    acceptInvite: false,
    registerNewUser: false
  },

  credentials: {
    email: '',
    password: '',
    isPersistent: false,
  },

  twoFactorAuth: {
    status: 0,
    phone: '',
    ddd: '',
    email: '',
    verificationType: 0,
    verificationTypeBackup: 0,
    token: {
      public: '',
      private: ''
    },
    selectedOptions: null
  },


  newUser: {
    name: '',
    occupation: null,
    otherOccupation: '',
    token: '',
    password: '',
  },

  errorList: [],
  errorMessage: '',
}

export const getters: GetterTree<AccountState, StoreState> = {
  userFirstName: (state) => state.user.name.split(' ')[0],
  userShortName: (state) => state.user.name.split(' ').slice(0, 1).join(' '),
  userFancyName: (state) => state.user.name.split(' ').slice(0, 2).join(' '),
}

export const mutations: MutationTree<AccountState> = {
  request_start(state, type) {
    state.requesting[type] = true
  },

  request_failure(state, { type, errorList }) {
    state.requesting[type] = false
    state.errorList = errorList
  },

  request_success(state, type) {
    state.requesting[type] = false
  },

  set_info(state, { authenticated, token }) {
    state.authenticated = authenticated
    state.token = token
  },

  set_company_info(state, company) {
    state.company.name = company.name
    state.company.logo = company.logo
    state.company.rate = company.rate
    state.company.waitingRegistration = company.waitingRegistration
  },

  set_user_info(state, user) {
    state.user.id = user.id
    state.user.name = user.name
    state.user.email = user.email
    state.user.avatar = user.avatar
    state.user.rate = user.rate
    state.user.userAuthenticatedRole = user.userAuthenticatedRole
  },

  set_credential(state, credentials) {
    state.credentials = Object.assign(state.credentials, credentials)
  },

  set_error(state, errorList) {
    state.errorList = errorList
  },

  enable_company(state, id) {
    state.company.isActive = true
    state.company.id = id
  },

  set_info_two_factor_auth(state, twoFactorAuth) {
    state.twoFactorAuth = Object.assign(state.twoFactorAuth, twoFactorAuth)
  },

  set_verification_2fa_type(state, verificationType) {
    state.twoFactorAuth.verificationType = verificationType
  },

  set_backup_verification_2fa_type(state, verificationTypeBackup) {
    state.twoFactorAuth.verificationTypeBackup = verificationTypeBackup
  },

  set_private_token_2fa(state, privateToken) {
    state.twoFactorAuth.token.private = privateToken
  },

  set_public_token_2fa(state, publicToken) {
    state.twoFactorAuth.token.public = publicToken
  },

  set_status_2fa(state, status) {
    state.twoFactorAuth.status = status
  },

  set_selected_options_2fa(state, options) {
    state.twoFactorAuth.options = options
  },
  set_status(state, status) {
    state.status = Object.assign(state.status, status)
  },

  set_new_user(state, newUser) {
    Object.assign(state.newUser, newUser)
  },

  set_validation(state, { errorList, isDirty, errorMessage }) {
    state.errorList = errorList
    state.errorMessage = errorMessage
    state.status.isDirty = isDirty
  },
}

export const actions: ActionTree<AccountState, StoreState> = {
  updateCredentials({ commit }, credentials: AccessCredentials) {
    commit('set_credential', credentials)
  },

  updateUserInfo({ commit }, auth: AccessLoginResult) {
    commit('set_user_info', auth)
  },

  updateCompanyInfo({ commit, dispatch }, auth: AccessLoginResult) {
    if (auth.companyId === null)
      return
    const { companyId } = auth
    dispatch('enableCompany', companyId)
    const { companyName: name, companyLogo: logo, companyRate: rate, waitingRegistration } = auth
    commit('set_company_info', { name, logo, rate, waitingRegistration })
  },

  updateOnboardingStatus({ commit }, auth: AccessLoginResult) {
    if (auth.isFirstAccess === false)
      return null

    const enumerator = enableOnboarding({
      isCompany: auth.companyId !== null
    })

    return enumerator
  },

  enableJsonToken({ commit }, auth: AccessLoginResult) {
    const { token } = auth
    const authenticated = true

    commit('set_info', { token, authenticated })
    jsonWebToken.updateToken(auth.token)
  },

  disableJsonToken({ commit }) {
    const token = null
    const authenticated = false

    commit('set_info', { token, authenticated })

    jsonWebToken.removeToken()
  },

  async login({ state, commit, dispatch }) {
    commit('request_start', 'login')

    try {
      const {data} = await accountApi.login(state.credentials)
      const auth = parseApiAuth(data)

      dispatch('updateUserInfo', auth)
      dispatch('updateCompanyInfo', auth)
      dispatch('updateOnboardingStatus', auth)
      dispatch('enableJsonToken', auth)

      commit('request_success', 'login')

      return data
    } catch (err) {
      const type = 'login'
      const errorList = apiErrorList(err)

      commit('request_failure', { type, errorList })

      throw err
    }
  },

  async logout({ commit, dispatch }) {
    commit('request_start', 'logout')

    try {
      const result = await accountApi.logout()
      dispatch('disableJsonToken')

      commit('request_success', 'logout')

      return result
    } catch (err) {
      const type = 'logout'
      const errorList = apiErrorList(err)

      commit('request_failure', { type, errorList })

      throw err
    }
  },

  async refreshAccountInfo() {
    try {
      const result = await updateToken()

      return result
    } catch (err) {
      throw err
    }
  },

  updateAuthWithUserIdentity({ dispatch }, authList: string[]) {
    const auth = parseEncodedAuth(authList)

    dispatch('updateUserInfo', auth)
    dispatch('updateCompanyInfo', auth)

    if (auth.id !== null) {
      dispatch('enableJsonToken', auth)
    } else {
      dispatch('disableJsonToken')
    }
  },

  updateErrors({ commit }, errorList: string[]) {
    commit('set_error', errorList)
  },

  enableCompany({ commit }, id: number) {
    commit('enable_company', id)
  },

  async getInfoTwoFactorAuth({ commit }) {
    commit('request_start', 'twoFactorAuth')

    try {
      const { data } = await getInfoTwoFactorAuth()
      commit('set_info_two_factor_auth', data)

      return data
    } catch(error) {
      commit('request_failure', 'twoFactorAuth')
      const genericErrorMessage = apiErrorList(error)

      throw genericErrorMessage
    } finally {
      commit('request_success', 'twoFactorAuth')
    }
  },

  async disableTokenTwoFactorAuth({ commit, state, dispatch }, password) {
    commit('request_start', 'twoFactorAuth')

    try {
      const data = await disableTokenTWoFactorAuth(state.user.email, password)
      dispatch('getStatusTokenTWoFactorAuth')
      return data
    } catch(error) {
      commit('request_failure', 'twoFactorAuth')

      throw error
    } finally {
      commit('request_success', 'twoFactorAuth')
    }
  },

  async registerTokenSendingMethod({ commit }) {
    commit('request_start', 'twoFactorAuth')

    try {
      const { data } = await registerTokenSendingMethod(state.twoFactorAuth.verificationType, state.twoFactorAuth.verificationTypeBackup)
      commit('set_private_token_2fa', data)

      return data
    } catch(err) {
      commit('request_failure', 'twoFactorAuth')

      throw err
    } finally {
      commit('request_success', 'twoFactorAuth')
    }

  },

  async setVerificationTypeTwoFactorAuth({ commit }, verificationType) {
    commit('set_verification_2fa_type', verificationType)
  },

  async setVerificationTypeBackupTwoFactorAuth({ commit }, verificationBackupType) {
    commit('set_backup_verification_2fa_type', verificationBackupType)
  },

  async activateTokenTwoFactorAuth({ commit, state, dispatch }, publicToken) {
    commit('request_start', 'twoFactorAuth')
    try {
      commit('set_public_token_2fa', publicToken)
      const data = await validateTokenTWoFactorAuth(state.twoFactorAuth.token)

      dispatch('getStatusTokenTWoFactorAuth')

      return data
    } catch(error) {
      commit('request_failure', "twoFactorAuth")

      throw error
    } finally {
      commit('request_success', 'twoFactorAuth')
      localStorage.removeItem('last-time-token-were-sent')
    }
  },

  async loginTokenTWoFactorAuth({ commit, state, dispatch }, info) {
    let { publicToken, isBackup } = info

    commit('request_start', 'twoFactorAuth')

    try {
      commit('set_public_token_2fa', publicToken)
      const { data } = await loginTokenTWoFactorAuth(state.twoFactorAuth.token, isBackup)
      dispatch('getStatusTokenTWoFactorAuth')

      return data
    } catch(error) {
      commit('request_failure', "twoFactorAuth")

      throw error
    } finally {
      commit('request_success', 'twoFactorAuth')
      localStorage.removeItem('last-time-token-were-sent')
    }
  },

  async sendTokenToLogin({ commit, state, dispatch }, isBackup = false) {

    commit('request_start', 'twoFactorAuth')

    try {
      const { data } = await sendTokenToLogin(isBackup)
      commit('set_private_token_2fa', data)

      return data
    } catch(error) {
      commit('request_failure', "twoFactorAuth")

      throw error
    } finally {

      commit('request_success', 'twoFactorAuth')
    }
  },

  async setBackupTokenTWoFactorAuth({ commit, dispatch }, selectedOption) { // VER O QUE FAZER COM ESSA FUNCAO
    commit('request_start', 'twoFactorAuth')
    try {
      const data = await setBackupTokenTWoFactorAuth(selectedOption)
      commit('set_backup_verification_2fa_type', selectedOption)
      dispatch('getStatusTokenTWoFactorAuth')

      return data
    } catch(error) {
      commit('request_failure', "twoFactorAuth")

      throw error
    } finally {
      commit('request_success', 'twoFactorAuth')
    }
  },

  async setDefaultOption({commit}, selectedOption) {
    const parsedOption = parseSelectedOptionTwoFactorAuth(selectedOption.defaultOption2FA)

    return commit('set_verification_2fa_type', parsedOption)
  },

  async setBackupOption({commit}, selectedOption) {
    const parsedOption = parseSelectedOptionTwoFactorAuth(selectedOption.backupOption2FA)

    return commit('set_backup_verification_2fa_type', parsedOption)
  },

  async getStatusTokenTWoFactorAuth({ commit }) {
    commit('request_start', 'twoFactorAuth')
    try {
      const { data } = await getStatusTokenTWoFactorAuth()

      commit('set_status_2fa', data.status)

      return data
    } catch(error) {
      commit('request_failure', "twoFactorAuth")

      throw error
    } finally {
      commit('request_success', 'twoFactorAuth')
    }
  },

  async getSelectedTwoFactorOptions({ commit }){
    commit('request_start', 'twoFactorAuth')
    try {
      const { data } = await getSelectedTwoFactorOptions()

      commit('set_selected_options_2fa', data)

      commit('request_success', 'twoFactorAuth')

      return data
    } catch (error) {
      commit('request_failure', "twoFactorAuth")

      throw error
    }

  },

  async acceptInvite({ commit }, accepted) {
    commit('request_start', 'acceptInvite')

    try {
      const { data } = await acceptInvite(accepted)

      commit('request_success', 'acceptInvite')

      return data
    } catch (error) {
      commit('request_failure', "acceptInvite")

      throw error
    }
  },

  async registerNewUser({ commit }, newUser) {
    commit('request_start', 'registerNewUser')

    try {
      const { data } = await inviteRegister(newUser)

      commit('request_success', 'registerNewUser')

      return data
    } catch (error) {
      commit('request_failure', "registerNewUser")

      throw error
    }
  },

  updateNewUser({ commit }, newUser) {
    commit('set_new_user', newUser)
  },

  updateStatus({ commit }, status) {
    commit('set_status', status)
  },

  updateValidation({ commit, state }, options) {
    const isDefined = (value) => typeof value !== 'undefined'

    const errorList = isDefined(options.errorList)
      ? options.errorList
      : state.errorList

    const isDirty = isDefined(options.isDirty)
      ? options.isDirty
      : state.status.isDirty

    const errorMessage = isDefined(options.errorMessage)
      ? options.errorMessage
      : state.errorMessage

    const validation = {
      errorList,
      isDirty,
      errorMessage,
    }

    commit('set_validation', validation)
  },
}

export default {
  namespaced: true,

  state,
  getters,
  actions,
  mutations,
}
